/**
 * A dashboard or report shown read-only to someone outside the agency: a
 * client in the portal, or a visitor holding a share link. They can move
 * between pages, present a report full screen and (when allowed) change the
 * period; nothing here can change the report itself.
 */

import { useState, type ReactNode } from 'react';

import { AudienceContext } from '../reports/audience.ts';
import { ChangeFormatContext, changeFormatOf } from '../reports/change-format.ts';
import { DateControl } from '../reports/DateControl.tsx';
import { PAGE_FORMAT } from '../reports/grid.ts';
import { coverFacts, type CoverFacts } from '../reports/PageArt.tsx';
import { PageTabs } from '../reports/PageTabs.tsx';
import { PageView } from '../reports/PageView.tsx';
import { Present } from '../reports/Present.tsx';
import { ReportPages } from '../reports/ReportPages.tsx';
import type {
  Catalogue,
  CompareSetting,
  DocumentKind,
  Page,
  RangeSetting,
} from '../reports/reports-api.ts';
import { ThemeScope } from '../reports/ThemeScope.tsx';
import type { Branding } from './portal-api.ts';
import { useReadOnlyPage, type QueryPage } from './use-read-only-page.ts';

const NO_OP = () => undefined;
const DEFAULT_RANGE: RangeSetting = { preset: 'LAST_MONTH' };

export interface ReadOnlyDocument {
  /** Distinguishes reports for re-querying (a document id, or the share link). */
  key: string;
  name: string;
  kind: DocumentKind;
  settings: { range?: RangeSetting; compare?: CompareSetting };
  theme?: Record<string, unknown> | undefined;
}

interface Shared {
  document: ReadOnlyDocument;
  pages: Page[];
  range: RangeSetting;
  compare: CompareSetting | undefined;
  catalogue: Catalogue;
  query: QueryPage;
  cover: (period: { from: string; to: string } | null) => CoverFacts;
}

function ShownPage(props: Shared & { index: number; width?: number | undefined }): ReactNode {
  const page = props.pages[props.index] as Page;
  const { results, period, error } = useReadOnlyPage({
    reportKey: props.document.key,
    query: props.query,
    page: props.index,
    range: props.range,
    compare: props.compare,
  });
  const keyOf = (index: number) => page.widgets[index]?.id ?? `w${index}`;
  return (
    <>
      {error && (
        <div className="rb-banner" role="alert">
          {error}
        </div>
      )}
      <ChangeFormatContext.Provider value={changeFormatOf(props.compare)}>
        <ThemeScope
          theme={props.document.theme}
          layout={PAGE_FORMAT[props.document.kind] ? 'report' : 'dashboard'}
        >
          <PageView
            kind={props.document.kind}
            page={page}
            pages={props.pages}
            cover={props.cover(period)}
            fixedWidth={props.width}
            canvas={{
              widgets: page.widgets,
              results,
              sources: props.catalogue.sources,
              editable: false,
              autoAlign: false,
              selected: null,
              onSelect: NO_OP,
              onChange: NO_OP,
              onRemove: NO_OP,
              keyOf,
            }}
          />
        </ThemeScope>
      </ChangeFormatContext.Provider>
    </>
  );
}

export function ReadOnlyReport(props: {
  document: ReadOnlyDocument;
  pages: Page[];
  clientName: string;
  branding: Branding;
  catalogue: Catalogue;
  query: QueryPage;
  /** False on a share link that keeps its own period. */
  canChangeRange: boolean;
  /** Shown before the title, e.g. a Back button. */
  leading?: ReactNode;
}): ReactNode {
  const { document, pages } = props;
  const [index, setIndex] = useState(0);
  const [range, setRange] = useState<RangeSetting>(document.settings.range ?? DEFAULT_RANGE);
  const [compare, setCompare] = useState<CompareSetting | undefined>(document.settings.compare);
  const [presenting, setPresenting] = useState(false);
  const format = PAGE_FORMAT[document.kind];
  const cover = (period: { from: string; to: string } | null): CoverFacts =>
    coverFacts({
      reportName: document.name,
      clientName: props.clientName,
      period,
      preset: range.preset,
      branding: props.branding,
    });
  const shared: Shared = {
    document,
    pages,
    range,
    compare,
    catalogue: props.catalogue,
    query: props.query,
    cover,
  };

  // Everyone here is outside the agency: agency-only notes ("attach an account") never show.
  return (
    <AudienceContext.Provider value="client">
      <div className="pt-viewer">
        <header className="pt-viewer-bar">
          {props.leading}
          <h1>{document.name}</h1>
          <span className="pt-spacer" />
          {props.canChangeRange && (
            <DateControl
              range={range}
              onRange={setRange}
              compare={compare ?? { mode: 'PREVIOUS_PERIOD' }}
              onCompare={setCompare}
            />
          )}
          {format && (
            <button type="button" className="btn small" onClick={() => setPresenting(true)}>
              ▶ Present
            </button>
          )}
        </header>
        <div className={`rb-body${format ? ' rb-body-report' : ''}`}>
          {format && (
            <ReportPages
              pages={pages}
              active={index}
              editable={false}
              onSelect={setIndex}
              onChange={NO_OP}
            />
          )}
          <main className="rb-main">
            {!format && pages.length > 1 && (
              <PageTabs
                pages={pages}
                active={index}
                editable={false}
                onSelect={setIndex}
                onChange={NO_OP}
              />
            )}
            <div
              className={`rb-stage rb-stage-${format ? document.kind.toLowerCase() : 'desktop'}`}
            >
              <ShownPage {...shared} index={index} />
            </div>
          </main>
        </div>
        {presenting && format && (
          <Present
            count={pages.length}
            ratio={format.ratio}
            start={index}
            onClose={() => setPresenting(false)}
            renderPage={(at, width) => (
              <ShownPage key={pages[at]?.id ?? at} {...shared} index={at} width={width} />
            )}
          />
        )}
      </div>
    </AudienceContext.Provider>
  );
}
